import { CodeEditorProfile, CodeEditorProfileId, CodeEditorSnippet } from "./types";

const backendSharedLib = `
interface PrimumRecord {
  [key: string]: unknown;
}
interface PrimumTargetRecord extends PrimumRecord {}
/**
 * Execution log available inside platform JavaScript runtimes.
 * Use it from the handler parameter, for example: async function handler({ log }) { log.info("message"); }
 */
interface PrimumLog {
  /** Stores an informational message in the execution log. */
  info(message: string, meta?: unknown): void;
  /** Stores a warning message in the execution log. */
  warn(message: string, meta?: unknown): void;
  /** Stores an error message in the execution log. */
  error(message: string, meta?: unknown): void;
}

/**
 * Utility helpers available inside platform JavaScript runtimes.
 * Use it from the handler parameter, for example: async function handler({ utils }) { return utils.uuid(); }
 */
interface PrimumUtils {
  /** Returns the current timestamp as an ISO string. */
  now(): string;
  /** Generates a random UUID. */
  uuid(): string;
  /** Generates a SHA-256 hash from the provided value. */
  sha256(value: unknown): string;
  /** Generates an HMAC SHA-256 hash from the provided value and secret. */
  hmacSha256(value: unknown, secret: string): string;
}

type PrimumDbOrderDirection = "asc" | "desc";
type PrimumDbQueryOperator =
  | "eq"
  | "neq"
  | "in"
  | "contains"
  | "not_contains"
  | "stw"
  | "edw"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  | "empty"
  | "not_empty"
  | "eq_dynamic"
  | "neq_dynamic"
  | "gt_dynamic"
  | "gte_dynamic"
  | "lt_dynamic"
  | "lte_dynamic";

interface PrimumDbConditionWhere {
  /** Condition node for a direct field or reference path such as "sys_table_id.name". */
  type: "condition";
  /** Field name or dot-walked reference path. */
  field: string;
  /** Query operator. Use "stw" for starts with and "edw" for ends with. */
  operator: PrimumDbQueryOperator;
  /** Value required by the operator. Omit for empty/not_empty. */
  value?: unknown;
}

interface PrimumDbLogicalWhere {
  /** Logical group node. */
  type: "logical";
  /** Combines child conditions with AND or OR. */
  operator: "and" | "or";
  /** Nested conditions. */
  conditions: PrimumDbWhere[];
}

interface PrimumDbExistsWhere {
  /** EXISTS query against another table. */
  type: "exists";
  /** Related table to query. */
  table: string;
  /** Conditions evaluated inside the EXISTS query. */
  conditions: PrimumDbConditionWhere[];
}

type PrimumDbAdvancedWhere = PrimumDbConditionWhere | PrimumDbLogicalWhere | PrimumDbExistsWhere;
type PrimumDbLegacyWhere = Record<string, unknown>;
type PrimumDbWhere = PrimumDbLegacyWhere | PrimumDbAdvancedWhere;

interface PrimumDbOrder {
  /** Field name or dot-walked reference path, for example "sys_table_id.name". */
  field: string;
  /** Sort direction. Defaults to asc. */
  direction?: PrimumDbOrderDirection;
}

interface PrimumDbFirstInput {
  /** Simple field/value filters or an advanced query-engine node. */
  where?: PrimumDbWhere;
  /** Ordered list of sorting rules. Multiple fields are applied in order. */
  order?: PrimumDbOrder[];
}

interface PrimumDbAllInput extends PrimumDbFirstInput {
  /** Maximum number of records to return. Use null to keep the runtime default. */
  limit?: number | null;
}

interface PrimumDbUpdateInput<TRecord extends PrimumRecord = PrimumRecord> {
  /** Simple field/value filters or an advanced query-engine node used to select records. */
  where?: PrimumDbWhere;
  /** Attributes to write to every matching record. */
  attributes?: Partial<TRecord> & PrimumRecord;
}

interface PrimumDbDeleteInput {
  /** Simple field/value filters or an advanced query-engine node used to select records. */
  where?: PrimumDbWhere;
}

interface PrimumDbCountInput {
  /** Simple field/value filters or an advanced query-engine node used to count records. */
  where?: PrimumDbWhere;
}

/**
 * Table-specific database operations returned by db.table("table_name").
 * All read/write calls are asynchronous because they cross from JavaScript to Rails through RPC.
 */
interface PrimumDbTable<TRecord extends PrimumRecord = PrimumRecord> {
  /** Returns the first record matching the optional filter and order. */
  first(input?: PrimumDbFirstInput): Promise<TRecord | null>;
  /** Returns records matching the optional filter, order and limit. */
  all(input?: PrimumDbAllInput): Promise<TRecord[]>;
  /** Counts records matching the optional filter without loading full records into JavaScript. */
  count(input?: PrimumDbCountInput): Promise<number>;
  /** Inserts a record and returns the created row. */
  insert(attributes: Partial<TRecord> & PrimumRecord): Promise<TRecord>;
  /** Updates all matching records and returns the updated rows. */
  update(input?: PrimumDbUpdateInput<TRecord>): Promise<TRecord[]>;
  /** Deletes all matching records and returns deleted record ids. */
  delete(input?: PrimumDbDeleteInput): Promise<string[]>;
}

/**
 * Database API available from handler({ db }).
 * Start with db.table("logical_table_name") and continue with first/all/count/insert/update/delete.
 */
interface PrimumDb {
  /** Selects a logical platform table and returns its table operation API. */
  table(name: string): PrimumDbTable;
}

interface PrimumHttpOptions {
  /** Absolute URL to call. */
  url?: string;
  /** HTTP method used by request(). Helper methods set this automatically. */
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | string;
  /** Request headers. */
  headers?: Record<string, string>;
  /** Request body. */
  body?: unknown;
  /** Timeout or transport-specific options accepted by the backend runtime. */
  [key: string]: unknown;
}

/**
 * HTTP client available from handler({ http }).
 * Every method returns the parsed response value produced by the backend RPC layer.
 */
interface PrimumHttp {
  /** Sends a request using the provided options object. */
  request(options: PrimumHttpOptions): Promise<unknown>;
  /** Sends an HTTP GET request. */
  get(url: string, options?: Omit<PrimumHttpOptions, "url" | "method">): Promise<unknown>;
  /** Sends an HTTP POST request. */
  post(url: string, options?: Omit<PrimumHttpOptions, "url" | "method">): Promise<unknown>;
  /** Sends an HTTP PUT request. */
  put(url: string, options?: Omit<PrimumHttpOptions, "url" | "method">): Promise<unknown>;
  /** Sends an HTTP PATCH request. */
  patch(url: string, options?: Omit<PrimumHttpOptions, "url" | "method">): Promise<unknown>;
  /** Sends an HTTP DELETE request. */
  delete(url: string, options?: Omit<PrimumHttpOptions, "url" | "method">): Promise<unknown>;
}

interface PrimumSecretOptions {
  label?: string;
  description?: string;
  [key: string]: unknown;
}

/**
 * Secret store API available from handler({ secrets }).
 * Availability depends on the script capabilities configured in metadata.
 */
interface PrimumSecrets {
  /** Reads a named secret. */
  get(key: string): Promise<unknown>;
  /** Writes a named secret value. */
  set(key: string, value: unknown, options?: PrimumSecretOptions): Promise<unknown>;
}

interface PrimumEmailRelatedRecord {
  /** Logical table name related to the email message. */
  table: string;
  /** Record id related to the email message. */
  id: string;
  /** Optional relationship type persisted in sys_email_record_links. */
  type?: "source" | "target" | "related" | string;
}

interface PrimumEmailSendInput {
  /** Optional sys_email_accounts.code. When omitted, the default outbound active account is used. */
  account?: string;
  /** Optional sender address override. Defaults to the selected account address. */
  from?: string;
  /** Recipient address or addresses. */
  to: string | string[];
  /** CC recipient address or addresses. */
  cc?: string | string[];
  /** BCC recipient address or addresses. */
  bcc?: string | string[];
  /** Email subject. */
  subject: string;
  /** HTML email body. */
  html?: string;
  /** Plain text email body. */
  text?: string;
  /** Optional record link used for tracking this email from a record. */
  related?: PrimumEmailRelatedRecord;
  /** Existing sys_attachments ids to send with the message. */
  attachments?: Array<{ id: string } | string>;
}

interface PrimumEmailTemplateInput {
  /** Optional sys_email_accounts.code. When omitted, the default outbound active account is used. */
  account?: string;
  /** sys_email_templates.code or template id. */
  template: string;
  /** Recipient address or addresses. */
  to: string | string[];
  /** CC recipient address or addresses. */
  cc?: string | string[];
  /** BCC recipient address or addresses. */
  bcc?: string | string[];
  /** Record used to resolve template placeholders and create tracking links. */
  record?: PrimumEmailRelatedRecord;
  /** Alias accepted by the runtime for record tracking. */
  related?: PrimumEmailRelatedRecord;
  /** Extra variables available to template placeholders. */
  data?: Record<string, unknown>;
  /** Optional plain text body override. */
  text?: string;
  /** Existing sys_attachments ids to send with the message. */
  attachments?: Array<{ id: string } | string>;
}

interface PrimumEmailRenderedTemplate {
  /** Rendered subject. */
  subject: string;
  /** Rendered HTML body. */
  html: string;
  /** Optional plain text body. */
  text?: string;
}

interface PrimumEmailConnectionTestInput {
  /** sys_email_accounts id or code. */
  account: string;
}

interface PrimumEmailConnectionTestResult {
  ok: boolean;
  message: string;
  account?: PrimumRecord;
}

interface PrimumAttachmentRecord {
  id: string;
  table_name: string;
  record_id: string;
  column_name?: string | null;
  filename: string;
  content_type?: string | null;
  byte_size: number;
  checksum?: string | null;
  status: string;
  visibility: string;
  uploaded_by_user_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface PrimumAttachmentTarget {
  /** Logical table name that owns the attachment. */
  table: string;
  /** Record id that owns the attachment. */
  record_id: string;
  /** Optional ATTACHMENT column name. Omit for record-level attachments. */
  column?: string | null;
}

interface PrimumAttachmentLookupInput extends PrimumAttachmentTarget {
  /** sys_attachments id. */
  attachment_id: string;
}

interface PrimumAttachmentReadResult {
  attachment: PrimumAttachmentRecord;
  /** Base64-encoded file bytes. */
  base64: string;
  content_type: string;
  filename: string;
  byte_size: number;
}

interface PrimumAttachmentCreateBase64Input extends PrimumAttachmentTarget {
  filename: string;
  content_type?: string;
  /** Base64-encoded file bytes. */
  base64: string;
}

/**
 * Attachment runtime available in backend JavaScript runtimes.
 * Requires attachment.read:<table> for list/get/read and attachment.write:<table> for createBase64/delete.
 */
interface PrimumAttachments {
  /** Lists record-level or field-level attachments. */
  list(input: PrimumAttachmentTarget): Promise<PrimumAttachmentRecord[]>;
  /** Reads attachment metadata. */
  get(input: PrimumAttachmentLookupInput): Promise<PrimumAttachmentRecord>;
  /** Reads attachment metadata plus base64 content. */
  read(input: PrimumAttachmentLookupInput): Promise<PrimumAttachmentReadResult>;
  /** Creates an attachment from base64 content. */
  createBase64(input: PrimumAttachmentCreateBase64Input): Promise<PrimumAttachmentRecord>;
  /** Deletes an attachment using the attachment storage adapter and soft-delete metadata. */
  delete(input: PrimumAttachmentLookupInput): Promise<PrimumAttachmentRecord>;
}

interface PrimumVersionRecordRestoreInput {
  /** Logical table name of the record to restore. */
  table: string;
  /** Target record id to restore. */
  record_id: string;
  /** version_records id containing the snapshot to apply. */
  version_record_id: string;
}

/**
 * Version record runtime available in backend JavaScript runtimes.
 * Requires capability version_record.restore:<table>.
 */
interface PrimumVersionRecords {
  /** Restores a version-controlled record from a VersionRecord snapshot and returns the newly created VersionRecord. */
  restore(input: PrimumVersionRecordRestoreInput): Promise<PrimumRecord>;
}

interface PrimumFlussStartInstanceInput {
  /** Definition key of the Fluss process to start. Snake_case alias definition_key is accepted by the backend. */
  definitionKey: string;
  /** Optional start element id. Snake_case alias start_element_id is accepted by the backend. */
  startElementId?: string | null;
  /** Optional anchor table name. Snake_case alias anchor_table is accepted by the backend. */
  anchorTable?: string | null;
  /** Optional anchor record id. Snake_case alias anchor_record_id is accepted by the backend. */
  anchorRecordId?: string | null;
  /** Variables passed to the process instance. */
  variables?: Record<string, unknown>;
}

interface PrimumFlussStartInstanceResult {
  /** Started sys_fluss_instances record id. */
  instanceId: string;
  /** Current process instance status. */
  status: string;
}

/**
 * Fluss runtime available from handler({ fluss }).
 * Availability depends on fluss.start_instance:<definitionKey> capabilities_json entries.
 */
interface PrimumFluss {
  /** Starts a Fluss process instance. Requires capability "fluss.start_instance:<definitionKey>". */
  startInstance(input: PrimumFlussStartInstanceInput): Promise<PrimumFlussStartInstanceResult>;
}

/**
 * Email runtime available from handler({ email }).
 * Availability depends on email.* entries in capabilities_json.
 */
interface PrimumEmail {
  /** Creates an outbound message and queues SMTP delivery. Requires capability "email.send". */
  send(input: PrimumEmailSendInput): Promise<PrimumRecord>;
  /** Renders a sys_email_templates record, creates an outbound message and queues delivery. Requires capability "email.send_template". */
  sendTemplate(input: PrimumEmailTemplateInput): Promise<PrimumRecord>;
  /** Renders a sys_email_templates record without creating or sending a message. Requires capability "email.render_template". */
  renderTemplate(input: PrimumEmailTemplateInput): Promise<PrimumEmailRenderedTemplate>;
  /** Tests SMTP connectivity for a sys_email_accounts record. Requires capability "email.test_connection". */
  testConnection(input: PrimumEmailConnectionTestInput): Promise<PrimumEmailConnectionTestResult>;
}
`;

const businessRuleLib = `
${backendSharedLib}

interface BusinessRuleTable {
  id?: string;
  name?: string;
  label?: string;
}

interface BusinessRuleResult {
  __kind: "business_rule_result";
  status: "ok" | "skip" | "abort";
  current?: PrimumRecord;
  message?: string | null;
  [key: string]: unknown;
}

/**
 * Result helpers available from handler({ rule }).
 * Action scripts must return rule.ok(), rule.skip(), or rule.abort().
 */
interface BusinessRuleHelpers {
  /** Completes the rule successfully. Include { current } after mutating current. */
  ok(payload?: PrimumRecord): BusinessRuleResult;
  /** Skips the rule without aborting the record operation. */
  skip(message?: string | null, payload?: PrimumRecord): BusinessRuleResult;
  /** Aborts the record operation and exposes the message to the caller. */
  abort(message: string, payload?: PrimumRecord): BusinessRuleResult;
  /** Suppresses or restores Business Rule execution for subsequent db operations (insert, update, delete) within this script. When true, db operations will not trigger Business Rules on the target table. */
  suppress(value?: boolean): void;
}

/**
 * Parameters passed by the backend to every business rule handler.
 * Destructure only the objects you need: async function handler({ current, db, rule }) { ... }
 */
interface BusinessRuleEvents {
  /** Emits an active catalog event. Requires capability "events.emit". */
  emit(
    type: string,
    payload?: PrimumRecord,
    options?: { sourceTable?: string; sourceRecordId?: string; correlationKey?: string }
  ): Promise<{ id: string; status: string }>;
}

interface BusinessRuleHandlerContext {
  /** Current record values being processed. */
  current: PrimumTargetRecord;
  /** Previous record state, when available. */
  previous: PrimumTargetRecord;
  /** Changed fields for the current operation. */
  changes: Partial<PrimumTargetRecord>;
  /** Business rule event, such as before or after. */
  event: string;
  /** Record operation, such as create, update or delete. */
  operation: string;
  /** Metadata for the table being processed. */
  table: BusinessRuleTable;
  /** Current authenticated user, when available. */
  user: PrimumRecord | null;
  /** Database API. Available through the handler parameter. */
  db: PrimumDb;
  /** HTTP client. Available through the handler parameter. */
  http: PrimumHttp;
  /** Secret store. Available through the handler parameter. */
  secrets: PrimumSecrets;
  /** Email runtime. Availability depends on email.* capabilities_json entries. */
  email: PrimumEmail;
  /** Attachment runtime. Availability depends on attachment.* capabilities_json entries. */
  attachments: PrimumAttachments;
  /** Version record runtime. Availability depends on version_record.restore:<table> capabilities_json entries. */
  versionRecords: PrimumVersionRecords;
  /** Fluss runtime. Availability depends on fluss.start_instance:<definitionKey> capabilities_json entries. */
  fluss: PrimumFluss;
  /** Event bus emitter. Availability requires the exact events.emit capability. */
  events: BusinessRuleEvents;
  /** Execution logger. Available through the handler parameter. */
  log: PrimumLog;
  /** Utility helpers. Available through the handler parameter. */
  utils: PrimumUtils;
  /** Business rule result helpers. Available through the handler parameter. */
  rule: BusinessRuleHelpers;
}

interface BusinessRuleConditionHandler {
  (context: BusinessRuleHandlerContext): boolean | Promise<boolean>;
}

interface BusinessRuleActionHandler {
  (context: BusinessRuleHandlerContext): BusinessRuleResult | Promise<BusinessRuleResult>;
}

`;

const flussScriptTaskLib = `${businessRuleLib}
/** Process variables of this Fluss instance (generated from the data catalog). */
interface FlussProcessVariables { [key: string]: unknown; }
interface FlussScriptTaskCurrent extends PrimumTargetRecord {
  /** Variables of the running Fluss instance. */
  fluss_variables: FlussProcessVariables;
  /** Table of the record chosen in "Record", or null when the node has no record. */
  fluss_record_table: string | null;
  /** Always null in a Script Task. */
  fluss_action_params: null;
}
interface FlussScriptTaskOkPayload {
  /** Values exposed as variables of this node. Map them in "Output variables" (output.<key>). */
  outputs?: Record<string, unknown>;
  [key: string]: unknown;
}
interface FlussScriptTaskRuleHelpers extends BusinessRuleHelpers {
  /** Completes the node; outputs become node variables. */
  ok(payload?: FlussScriptTaskOkPayload): BusinessRuleResult;
  /** Completes the node with status "skip". */
  skip(message?: string | null, payload?: FlussScriptTaskOkPayload): BusinessRuleResult;
  /** Raises the Fluss incident script_task_aborted. */
  abort(message: string, payload?: FlussScriptTaskOkPayload): BusinessRuleResult;
}
interface FlussScriptTaskContext extends Omit<BusinessRuleHandlerContext, "current" | "previous" | "changes" | "event" | "operation" | "table" | "user" | "rule"> {
  /** Fields of the record chosen in "Record" plus the fluss_* runtime keys. */
  current: FlussScriptTaskCurrent;
  /** Always {} in a Script Task. */
  previous: {};
  /** Always {} in a Script Task. */
  changes: {};
  event: "fluss_script_task";
  operation: "execute";
  /** Table of the record, or null when the node has no record. */
  table: BusinessRuleTable | null;
  /** User that moved the token, when available. */
  user: { id: string } | null;
  rule: FlussScriptTaskRuleHelpers;
}
`;

export const FLUSS_SCRIPT_TASK_DEFAULT_SCRIPT = `/**
 * Runs when the Fluss token reaches this Script Task.
 * Keep the parameter line below: it enables autocomplete for the handler arguments.
 * @param {FlussScriptTaskContext} context
 */
async function handler({ current, db, http, secrets, email, attachments, events, fluss, log, utils, rule }) {
  const variables = current.fluss_variables;
  log.info("Script task started", { table: current.fluss_record_table });

  // Each key in outputs becomes a variable of this node.
  // Map it in "Output variables" (output.<key>) to use it in later nodes.
  return rule.ok({ outputs: {} });
}`;

const platformJobLib = `
${backendSharedLib}

interface PlatformJobSchedule {
  /** Schedule strategy selected in sys_jobs.schedule_type. */
  schedule_type?: "once" | "interval" | "hourly" | "daily" | "weekly" | "monthly" | "cron" | string;
  /** Numeric interval amount when schedule_type is interval. */
  interval_value?: number | string | null;
  /** Interval unit when schedule_type is interval. */
  interval_unit?: "minutes" | "hours" | "days" | "weeks" | "months" | string | null;
  /** Local time in HH:MM format for daily, weekly and monthly jobs. */
  time_of_day?: string | null;
  /** Day of week value, where 0 is Sunday and 6 is Saturday. */
  day_of_week?: string | number | null;
  /** Day of month value from 1 to 31. */
  day_of_month?: number | string | null;
  /** Cron expression when schedule_type is cron. */
  cron_expression?: string | null;
  /** Timezone used to interpret time_of_day. */
  timezone?: string | null;
  /** Next scheduled execution timestamp when available. */
  next_run_at?: string | null;
  /** Last execution timestamp when available. */
  last_run_at?: string | null;
}

interface PlatformJobExecution {
  /** sys_job_executions id for this run. */
  id?: string;
  /** Time this execution was scheduled for. */
  scheduled_for?: string | null;
  /** Execution trigger origin. */
  triggered_by?: "scheduler" | "manual" | "system" | string;
  /** Current attempt number. */
  attempt?: number | string | null;
}

interface PlatformJobResult {
  __kind: "platform_job_result";
  status: "ok" | "skip" | "fail";
  message?: string | null;
  [key: string]: unknown;
}

/**
 * Result helpers available from handler({ job }).
 * Action scripts must return job.ok(), job.skip(), or job.fail().
 */
interface PlatformJobHelpers {
  /** Completes the job successfully. */
  ok(payload?: PrimumRecord): PlatformJobResult;
  /** Skips this execution without treating it as a failure. */
  skip(message?: string | null, payload?: PrimumRecord): PlatformJobResult;
  /** Marks this execution as failed with a user-facing message. */
  fail(message: string, payload?: PrimumRecord): PlatformJobResult;
}

/**
 * Parameters passed by the backend to every Platform Job handler.
 * Destructure only the objects you need: async function handler({ job, db, log }) { ... }
 */
interface PlatformJobHandlerContext {
  /** Job result helpers. Available through the handler parameter. */
  job: PlatformJobHelpers;
  /** Database API. Availability depends on capabilities_json. */
  db: PrimumDb;
  /** HTTP client. Availability depends on capabilities_json. */
  http: PrimumHttp;
  /** Secret store. Availability depends on capabilities_json. */
  secrets: PrimumSecrets;
  /** Email runtime. Availability depends on email.* capabilities_json entries. */
  email: PrimumEmail;
  /** Attachment runtime. Availability depends on attachment.* capabilities_json entries. */
  attachments: PrimumAttachments;
  /** Version record runtime. Availability depends on version_record.restore:<table> capabilities_json entries. */
  versionRecords: PrimumVersionRecords;
  /** Fluss runtime. Availability depends on fluss.start_instance:<definitionKey> capabilities_json entries. */
  fluss: PrimumFluss;
  /** Execution logger persisted in sys_job_executions.logs_json. */
  log: PrimumLog;
  /** Utility helpers. */
  utils: PrimumUtils;
  /** Schedule metadata for the current job. */
  schedule: PlatformJobSchedule;
  /** Execution metadata for the current run. */
  execution: PlatformJobExecution;
  /** User associated with the job, when available. */
  user: PrimumRecord | null;
}

interface PlatformJobConditionHandler {
  (context: PlatformJobHandlerContext): boolean | Promise<boolean>;
}

interface PlatformJobActionHandler {
  (context: PlatformJobHandlerContext): PlatformJobResult | Promise<PlatformJobResult>;
}

`;

const dynamicApiLib = `
${backendSharedLib}

interface DynamicApiRequest {
  /** HTTP method used by the incoming request. */
  method: string;
  /** Matched request path. */
  path: string;
  /** Request headers. */
  headers: Record<string, string>;
  /** Query string values parsed by the backend. */
  query: Record<string, unknown>;
  /** Route params extracted from the endpoint path. */
  params: Record<string, string>;
  /** Parsed request body. */
  body: unknown;
  /** Raw request body. */
  rawBody: string;
}

interface DynamicApiAuth {
  mode: string;
  authenticated: boolean;
  subject: unknown;
  metadata: Record<string, unknown>;
}

interface DynamicApiResponseResult<TBody = unknown> {
  __kind: "response";
  format: "json" | "text" | "html" | "empty";
  status: number;
  headers: Record<string, string>;
  body: TBody;
}

/**
 * Response builder available from handler({ response }).
 * Dynamic API scripts must return one of these response objects.
 */
interface DynamicApiResponse {
  /** Returns a JSON response. */
  json(body: unknown, status?: number, headers?: Record<string, string>): DynamicApiResponseResult<unknown>;
  /** Returns a plain text response. */
  text(body: string, status?: number, headers?: Record<string, string>): DynamicApiResponseResult<string>;
  /** Returns an HTML response. */
  html(body: string, status?: number, headers?: Record<string, string>): DynamicApiResponseResult<string>;
  /** Returns an empty response, defaulting to status 204. */
  empty(status?: number, headers?: Record<string, string>): DynamicApiResponseResult<null>;
}

/**
 * Parameters passed by the backend to every Dynamic API handler.
 * Destructure only the objects you need: async function handler({ request, response, db }) { ... }
 */
interface DynamicApiHandlerContext {
  /** Incoming HTTP request context. */
  request: DynamicApiRequest;
  /** Response factory. Return response.json/text/html/empty from the handler. */
  response: DynamicApiResponse;
  /** Database API. Available through the handler parameter. */
  db: PrimumDb;
  /** HTTP client. Available through the handler parameter. */
  http: PrimumHttp;
  /** Secret store. Available through the handler parameter. */
  secrets: PrimumSecrets;
  /** Email runtime. Availability depends on email.* capabilities_json entries. */
  email: PrimumEmail;
  /** Attachment runtime. Availability depends on attachment.* capabilities_json entries. */
  attachments: PrimumAttachments;
  /** Version record runtime. Availability depends on version_record.restore:<table> capabilities_json entries. */
  versionRecords: PrimumVersionRecords;
  /** Execution logger. Available through the handler parameter. */
  log: PrimumLog;
  /** Utility helpers. Available through the handler parameter. */
  utils: PrimumUtils;
  /** Authentication result for this endpoint call. */
  auth: DynamicApiAuth;
  /** Route params extracted from the endpoint path. */
  params: Record<string, string>;
}

interface DynamicApiHandler {
  (context: DynamicApiHandlerContext): DynamicApiResponseResult | Promise<DynamicApiResponseResult>;
}

`;

const formActionLib = `
interface PrimumRecord {
  [key: string]: any;
}
interface PrimumTargetRecord extends PrimumRecord {}

interface ClientActionTable {
  id?: string;
  name?: string;
  label?: string;
}

interface ClientActionRouter {
  /** Navigates to an internal workspace route. */
  navigate(to: string | number, options?: { replace?: boolean; state?: unknown }): void;
  /** Refreshes the current workspace route. */
  refresh(): void;
  /** Current internal URL, including search params and hash. */
  currentUrl: string;
  /** Reads a safe return_to URL from the current route, when present. */
  getReturnTo(): string | null;
  /** Appends a safe return_to URL to another internal target. */
  withReturnTo(target: string, returnTo?: string | null): string;
  /** Navigates to return_to, or to a safe fallback when no return target exists. */
  returnTo(options?: { fallback?: string | null; replace?: boolean; state?: unknown }): boolean;
  /** Opens a record form by table and optional record id. */
  toRecord(tableName: string, recordId?: string | null, options?: { includeReturnTo?: boolean; replace?: boolean; state?: unknown }): void;
  /** Opens a table list. */
  toList(tableName: string, options?: { includeReturnTo?: boolean; replace?: boolean; state?: unknown }): void;
}

interface ClientActionModalProgressInput {
  title?: string;
  message?: string;
  detail?: string;
  value?: number;
  status?: "loading" | "success" | "error";
  steps?: string[];
  activeStep?: number;
  disableClose?: boolean;
  closeText?: string;
}

interface ClientActionModalProgressHandle {
  closed: Promise<{ action: string; data?: any }>;
  update(input: Partial<ClientActionModalProgressInput>): void;
  close(result?: { action: string; data?: any }): void;
}

interface ClientActionUi {
  /** Shows an alert notification. */
  alert(key: string, message: string): void;
  /** Shows a warning notification. */
  warning(key: string, message: string): void;
  /** Shows a success notification. */
  success(key: string, message: string): void;
  /** Shows an informational notification. */
  info(key: string, message: string): void;
  /** Opens workspace modals and resolves with the result selected by the user. */
  modal: {
    open(input: { widget?: string; widgetId?: string; title?: string; size?: "sm" | "md" | "lg" | "xl" | "fullscreen"; data?: Record<string, any> }): Promise<{ action: string; data?: any }>;
    alert(input: { title?: string; message?: string; confirmText?: string }): Promise<{ action: string; data?: any }>;
    confirm(input: { title?: string; message?: string; confirmText?: string; cancelText?: string }): Promise<{ action: string; data?: any }>;
    progress(input: ClientActionModalProgressInput): ClientActionModalProgressHandle;
  };
}

interface ClientDbFirstInput {
  where?: Record<string, any>;
  order?: Array<{ field: string; direction?: "asc" | "desc" }>;
}

interface ClientDbAllInput extends ClientDbFirstInput {
  limit?: number | null;
}

interface ClientDbTable<TRecord extends PrimumRecord = PrimumRecord> {
  /** Reads a record by id through the workspace Records API. */
  get(id: string): Promise<TRecord>;
  /** Returns the first record matching the optional filter and order. */
  first(input?: ClientDbFirstInput): Promise<TRecord | null>;
  /** Counts records matching the optional filter without loading the full result set. */
  count(input?: { where?: Record<string, any> }): Promise<number>;
  /** Returns records matching the optional filter, order and limit. */
  all(input?: ClientDbAllInput): Promise<TRecord[]>;
  /** Creates a record and returns the created row. */
  insert(attributes: Partial<TRecord> & PrimumRecord): Promise<TRecord>;
  /** Updates records matching the filter and returns updated rows. */
  update(input?: { where?: Record<string, any>; attributes?: Partial<TRecord> & PrimumRecord }): Promise<TRecord[]>;
  /** Deletes records matching the filter and returns deleted record ids. */
  delete(input?: { where?: Record<string, any> }): Promise<string[]>;
}

interface ClientDb {
  /** Selects a logical platform table and returns its browser-side record API. */
  table(name: string): ClientDbTable;
}

interface ClientCacheApi {
  /** Invalidates cached record lists/forms for one or more logical tables after custom HTTP work. */
  invalidateRecords(tables: string | string[]): void;
}

interface ClientPlatformPackageApi {
  addRecord(table: string, id: string): Promise<any>;
  removeRecord(id: string): Promise<any>;
  commit(packageId: string): Promise<any>;
  export(packageId: string): Promise<any>;
  convert(packageId: string): Promise<any>;
  rollback(packageId: string): Promise<any>;
}

interface ClientPlatformJobApi {
  /** Queues a platform job execution through the workspace runtime. */
  run(jobId: string): Promise<any>;
}

interface ClientEventApi {
  /** Requeues a dead Event Manager delivery through the authorized native endpoint. */
  retryDelivery(deliveryId: string): Promise<any>;
}

interface ClientSessionApi {
  isAlive(): Promise<any>;
  createUser(record: PrimumRecord): Promise<any>;
  updateUser(record: PrimumRecord): Promise<any>;
  updateUserPassword(userId: string, record: PrimumRecord): Promise<any>;
}

interface ClientMetadataApi {
  createTable(table: PrimumRecord): Promise<any>;
  deleteTable(id: string): Promise<any>;
  createColumn(column: PrimumRecord): Promise<any>;
  updateColumn(id: string, column: PrimumRecord): Promise<any>;
  deleteColumn(id: string): Promise<any>;
  createIndex(index: PrimumRecord): Promise<any>;
  deleteIndex(id: string): Promise<any>;
  buildIndex(id: string): Promise<any>;
}

interface ClientDynamicApi {
  /** Calls an authenticated Dynamic API endpoint under /api/ext. */
  post(path: string, payload?: any): Promise<any>;
}

interface ClientEmailApi {
  /** Sends an email through /api/ext/platform/email/send. */
  send(input: {
    account?: string;
    from?: string;
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    subject: string;
    html?: string;
    text?: string;
    related?: { table: string; id: string; type?: string };
    attachments?: Array<{ id: string } | string>;
  }): Promise<any>;
  /** Sends an email template through /api/ext/platform/email/send-template. */
  sendTemplate(input: {
    account?: string;
    template: string;
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    record?: { table: string; id: string; type?: string };
    related?: { table: string; id: string; type?: string };
    data?: Record<string, any>;
    text?: string;
    attachments?: Array<{ id: string } | string>;
  }): Promise<any>;
  /** Renders an email template through /api/ext/platform/email/render-template. */
  renderTemplate(input: {
    template: string;
    record?: { table: string; id: string; type?: string };
    related?: { table: string; id: string; type?: string };
    data?: Record<string, any>;
  }): Promise<any>;
  /** Tests an email account connection through /api/ext/platform/email/test-connection. */
  testConnection(input: { account: string }): Promise<any>;
}

interface ClientAttachmentRecord {
  id: string;
  table_name: string;
  record_id: string;
  column_name?: string | null;
  filename: string;
  content_type?: string | null;
  byte_size: number;
  checksum?: string | null;
  status: string;
  visibility: string;
  uploaded_by_user_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ClientAttachmentTarget {
  table: string;
  recordId: string;
  column?: string | null;
}

interface ClientDraftAttachmentTarget {
  table: string;
  draftToken: string;
  column?: string | null;
}

interface ClientAttachmentApi {
  /** Lists record-level or field-level attachments. */
  list(input: ClientAttachmentTarget): Promise<ClientAttachmentRecord[]>;
  /** Uploads a File to an existing record. */
  upload(input: ClientAttachmentTarget & { file: File }): Promise<ClientAttachmentRecord>;
  /** Deletes an attachment from an existing record. */
  delete(input: ClientAttachmentTarget & { attachmentId: string }): Promise<ClientAttachmentRecord>;
  /** Builds an authenticated download URL for an attachment. */
  downloadUrl(input: ClientAttachmentTarget & { attachmentId: string }): string;
  /** Builds an inline image view URL for an attachment. Public attachments can be viewed without authentication. */
  viewUrl(input: ClientAttachmentTarget & { attachmentId: string }): string;
  /** Draft attachment helpers for records that do not exist yet. */
  draft: {
    list(input: ClientDraftAttachmentTarget): Promise<ClientAttachmentRecord[]>;
    upload(input: ClientDraftAttachmentTarget & { file: File }): Promise<ClientAttachmentRecord>;
    delete(input: ClientDraftAttachmentTarget & { attachmentId: string }): Promise<ClientAttachmentRecord>;
    downloadUrl(input: ClientDraftAttachmentTarget & { attachmentId: string }): string;
    viewUrl(input: ClientDraftAttachmentTarget & { attachmentId: string }): string;
  };
}

interface ClientStudioTestAiConnectionResult {
  ok: boolean;
  message: string;
  raw_response: any;
}

/** Mirrors clientActionRuntime.ts createClientApi studio (lines 196-201), backed by POST /api/studio/ai/test_connection. */
interface ClientStudioApi {
  /** Tests the configured AI provider connection. */
  testAiConnection(input?: { model_id?: string; provider_id?: string }): Promise<ClientStudioTestAiConnectionResult>;
}

interface ClientActionApi {
  /** Browser-side Records API. Use server-side Business Rules or Dynamic APIs for privileged work. */
  db: ClientDb;
  /** Cache helpers for actions that mutate records outside client.db. */
  cache: ClientCacheApi;
  /** Platform package workflow helpers available in the workspace client. */
  platformPackage: ClientPlatformPackageApi;
  /** Platform job workflow helpers available in the workspace client. */
  platformJob: ClientPlatformJobApi;
  /** Event Manager operational helpers available to client-side Record Actions. */
  events: ClientEventApi;
  /** Authenticated user/session helpers available in the workspace client. */
  session: ClientSessionApi;
  /** Metadata helpers for sys_tables and sys_columns. */
  metadata: ClientMetadataApi;
  /** Authenticated Dynamic API helper for server-side scripts. */
  dynamicApi: ClientDynamicApi;
  /** Authenticated Studio AI helper backed by POST /api/studio/ai/test_connection. */
  studio: ClientStudioApi;
  /** Authenticated email helper backed by Dynamic API endpoints. */
  email: ClientEmailApi;
  /** Authenticated attachment helper backed by the Records attachment API. */
  attachments: ClientAttachmentApi;
}

interface ClientActionUtils {
  now(): string;
  uuid(): string;
}

interface ClientActionContext {
  /** Runtime mode: form, list, related_form or related_list. */
  mode: "form" | "list" | "related_form" | "related_list";
  /** Current form record. Empty for list actions. */
  current: PrimumTargetRecord;
  /** Selected records for list actions. Empty for form actions. */
  records: PrimumTargetRecord[];
  /** Current table metadata when available. */
  table: ClientActionTable;
  /** Current logical table name. */
  tableName: string;
  /** Current authenticated user session. */
  user?: any;
  /** Client-side platform APIs. */
  client: ClientActionApi;
  /** Workspace notifications. */
  ui: ClientActionUi;
  /** Workspace navigation. */
  router: ClientActionRouter;
  /** Client-safe utility helpers. */
  utils: ClientActionUtils;
}

interface ClientActionHandler {
  (context: ClientActionContext): void | Promise<void>;
}
`;

const clientScriptLib = formActionLib + `
interface ClientScriptFormApi {
  /** Reads the current form value for a field. */
  getValue(fieldName: string): any;
  /** Reads the text shown for a field in the requested language, or the current language when omitted or blank.
   * Unknown field: undefined. TRANSLATABLE_TEXT: empty/null becomes ""; a literal stays literal;
   * a translation key resolves to text, or "" if missing or resolved through language fallback.
   * During record editing, TRANSLATABLE_TEXT already localized for the current user keeps that
   * text even when another language is requested. Resolver errors propagate.
   * CHOICE: matching option label, translated when it is a key; missing option or translation: "".
   * REFERENCE: referenced record's display column in the requested language; missing record,
   * missing display value, or untranslated display key: "". Other fetch errors propagate.
   * Other types, including DYNAMIC_REFERENCE and PERMISSION_ARRAY, return the raw value.
   */
  getDisplayValue(fieldName: string, language?: string): Promise<any>;
  /** Updates a field value in the current form. */
  setValue(fieldName: string, value: any): void;
  /** Shows or hides a field in the current form. */
  setVisible(fieldName: string, visible: boolean): void;
  /** Makes a field required or optional in the current form. */
  setRequired(fieldName: string, required: boolean): void;
  /** Makes a field read-only or editable in the current form. */
  setReadonly(fieldName: string, readonly: boolean): void;
  /** Shows a form-level error notification. */
  addError(message: string): void;
  /** Shows a form-level info notification. */
  addInfo(message: string): void;
  /** Adds a validation error to a specific field. */
  addFieldError(fieldName: string, message: string): void;
  /** Clears validation errors for a specific field. */
  clearFieldError(fieldName: string): void;
  /** Adds an informational helper message to a specific field. */
  addFieldInfo(fieldName: string, message: string): void;
  /** Clears informational helper messages for a specific field. */
  clearFieldInfo(fieldName: string): void;
}

interface ClientScriptEvent {
  /** Current client script event type. */
  type: "onLoad" | "onSubmit" | "onChange";
  /** Changed field name for onChange scripts. */
  field?: string;
  /** Previous field value for onChange scripts. */
  oldValue?: any;
  /** New field value for onChange scripts. */
  newValue?: any;
}

interface ClientScriptContext extends ClientActionContext {
  /** Previous form values snapshot. Useful in onChange scripts. */
  previous: PrimumTargetRecord;
  /** Client-side form API for screen behavior. */
  form: ClientScriptFormApi;
  /** Current client script event payload. */
  event: ClientScriptEvent;
}

interface ClientScriptHandler {
  /** Return false from onSubmit to block the form action. */
  (context: ClientScriptContext): void | boolean | Promise<void | boolean>;
}
`;

const defaultValueScriptLib = formActionLib + `
interface DefaultValueScriptColumn {
  id: string;
  name: string;
  label: string;
  column_type: string;
  column_subtype?: string | null;
  referenced_table_name?: string | null;
  default_value?: any;
}

interface DefaultValueScriptContext extends ClientActionContext {
  /** Column whose default value is being calculated. */
  column: DefaultValueScriptColumn;
  /** Literal value configured in sys_columns.default_value before this script runs. */
  defaultValue: any;
}

interface DefaultValueScriptHandler {
  /** Return the field default value for new records. */
  (context: DefaultValueScriptContext): any | Promise<any>;
}
`;

const gateLib = `
interface GatePage {
  id?: string;
  name: string;
  slug: string;
  title?: string;
  widgets?: Array<Record<string, unknown>>;
  apiScope?: string[];
}

interface GateAuthApi {
  /** Reads the current public Gate session. */
  getSession(): Promise<unknown>;
  /** Authenticates a user with email/password credentials. */
  login(input: { email?: string; password?: string }): Promise<unknown>;
  /** Persists or clears remembered login values. */
  rememberLogin(input: { remember?: boolean; email?: string; password?: string }): Promise<unknown>;
  /** Reads remembered login values. */
  getRememberedLogin(): Promise<unknown>;
  /** Redirects to the host-resolved public login page with an optional return target. */
  loginRedirect(returnTo?: string): Promise<unknown>;
  /** Ends the current Gate session. */
  logout(): Promise<unknown>;
}

interface GateRecordQueryInput {
  table: string;
  query?: unknown;
  limit?: number;
  page?: number;
  perPage?: number;
  referenceMode?: string;
}

interface GateRecordApi {
  /** Queries records through the Gate bridge. */
  query(input: GateRecordQueryInput): Promise<unknown>;
  /** Reads a record by id. */
  get(input: { table: string; id: string }): Promise<unknown>;
  /** Creates a record. */
  create(input: { table: string; data: Record<string, unknown> }): Promise<unknown>;
  /** Updates a record by id. */
  update(input: { table: string; id: string; data: Record<string, unknown> }): Promise<unknown>;
  /** Deletes a record by id. */
  delete(input: { table: string; id: string }): Promise<unknown>;
}

interface GateSessionApi {
  createUser(input: Record<string, unknown>): Promise<unknown>;
  updateUser(input: Record<string, unknown>): Promise<unknown>;
  updatePassword(input: { userId: string; data: Record<string, unknown> }): Promise<unknown>;
}

interface GateEmailApi {
  send(input: {
    account?: string;
    from?: string;
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    subject: string;
    html?: string;
    text?: string;
    related?: { table: string; id: string; type?: string };
    attachments?: Array<{ id: string } | string>;
  }): Promise<unknown>;
  sendTemplate(input: {
    account?: string;
    template: string;
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    record?: { table: string; id: string; type?: string };
    related?: { table: string; id: string; type?: string };
    data?: Record<string, unknown>;
    text?: string;
    attachments?: Array<{ id: string } | string>;
  }): Promise<unknown>;
  renderTemplate(input: {
    template: string;
    record?: { table: string; id: string; type?: string };
    related?: { table: string; id: string; type?: string };
    data?: Record<string, unknown>;
  }): Promise<unknown>;
  uploadAttachment(input: { messageId?: string; file: File }): Promise<{ messageId: string; message?: unknown; attachment: GateAttachmentRecord }>;
  deleteAttachment(input: { messageId: string; attachmentId: string }): Promise<GateAttachmentRecord>;
}

interface GateAttachmentRecord {
  id: string;
  table_name: string;
  record_id: string;
  column_name?: string | null;
  filename: string;
  content_type?: string | null;
  byte_size: number;
  checksum?: string | null;
  status: string;
  visibility: string;
  uploaded_by_user_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface GateAttachmentInput {
  table: string;
  /** Existing record id. Use draftToken instead when the record is not created yet. */
  recordId?: string;
  /** Draft token for pre-save uploads. */
  draftToken?: string;
  /** Optional ATTACHMENT column name. Omit for record-level attachments. */
  column?: string | null;
}

interface GateAttachmentApi {
  /** Lists record-level, field-level, or draft attachments. Requires attachment.list. */
  list(input: GateAttachmentInput): Promise<GateAttachmentRecord[]>;
  /** Uploads a File selected in the Gate UI. Requires attachment.upload. */
  upload(input: GateAttachmentInput & { file: File }): Promise<GateAttachmentRecord>;
  /** Deletes an attachment. Requires attachment.delete. */
  delete(input: GateAttachmentInput & { attachmentId: string }): Promise<GateAttachmentRecord>;
  /** Builds a download URL for an attachment. Requires attachment.download_url. */
  downloadUrl(input: GateAttachmentInput & { attachmentId: string }): Promise<string>;
  /** Builds an inline image view URL for an attachment. Requires attachment.view_url. Public attachments can be viewed without authentication. */
  viewUrl(input: GateAttachmentInput & { attachmentId: string }): Promise<string>;
}

interface GateRouter {
  current: { pathname: string; search: string; hash: string; params: Record<string, string>; query: Record<string, string | string[]> };
  path: string;
  params: Record<string, string>;
  query: Record<string, string | string[]>;
  /** Navigates within the Gate host. */
  navigate(to: string, options?: { replace?: boolean; state?: unknown }): Promise<unknown>;
}

interface GateTheme {
  id?: string;
  name?: string;
  label?: string;
  mode?: "light" | "dark" | string;
  primary_seed_color?: string;
  secondary_seed_color?: string;
  neutral_seed_color?: string;
  background_seed_color?: string;
  success_seed_color?: string;
  warning_seed_color?: string;
  error_seed_color?: string;
  info_seed_color?: string;
  tonal_offset?: "subtle" | "standard" | "vivid" | string;
  contrast_level?: "comfortable" | "accessible" | "strict" | string;
  density?: "compact" | "standard" | "comfortable" | string;
  radius_base?: string | number;
  spacing_scale?: string | number;
  font_family?: string;
  font_size_base?: string | number;
  advanced?: boolean | string;
  palette_overrides_json?: string | Record<string, unknown> | null;
  component_overrides_json?: string | Record<string, unknown> | null;
  show_logo_wordmark?: boolean | string;
  logo_mark_url?: string | null;
  logo_wordmark_url?: string | null;
}

interface GateRuntime {
  invoke(method: string, payload?: unknown): Promise<unknown>;
  api: {
    auth: GateAuthApi;
    record: GateRecordApi;
    email: GateEmailApi;
    attachments: GateAttachmentApi;
    session: GateSessionApi;
  };
  router: GateRouter;
  modal: {
    /** Data passed by ui.modal.open({ data }). */
    data: Record<string, any>;
    /** Resolves the opening script promise with action confirm and the provided data. */
    submit(data?: any): Promise<unknown>;
    /** Resolves the opening script promise with action close, or the supplied action. */
    close(result?: { action?: string; data?: any }): Promise<unknown>;
    /** Resolves the opening script promise with action cancel. */
    cancel(data?: any): Promise<unknown>;
  };
  ui: {
    /** Shows a Gate notification. */
    notify(message: string, level?: "info" | "success" | "warning" | "error" | string): Promise<unknown>;
  };
  i18n: {
    locale: string;
    fallbackLocale?: string;
    messages: Record<string, unknown>;
    messagesByLocale?: Record<string, Record<string, unknown>>;
    has(key: string, localeOrOptions?: string | { locale?: string; language?: string }): boolean;
    t(key: string, params?: Record<string, unknown>, fallback?: string, localeOrOptions?: string | { locale?: string; language?: string }): unknown;
  };
  /** Active platform theme metadata. CSS variables are also available as --prm-primary, --prm-bg, --prm-surface, --prm-text-primary, --prm-border and related tokens. */
  theme: GateTheme;
  page: GatePage;
  widgets?: Record<string, unknown>;
}

/** Vue global runtime injected by Gate. */
declare const Vue: any;
/** Gate runtime API injected as a compile parameter and exposed on window.Gate. */
declare const Gate: GateRuntime;
/** Current Gate page definition injected as a compile parameter. */
declare const page: GatePage;
`;

const servicePortalFormScriptLib = gateLib + `
interface ServicePortalChoiceOption {
  /** Stored option value submitted by the form. */
  value: string;
  /** Display label shown to the user. Translation keys are resolved by the runtime. */
  label: string;
}

interface ServicePortalFormApi {
  /** Reads the current value of one form field. */
  getValue(fieldName: string): any;
  /** Reads a shallow copy of all current form values. */
  getValues(): Record<string, any>;
  /** Updates one form field value and fires change listeners when the value changes. */
  setValue(fieldName: string, value: any): void;
  /** Updates multiple form field values. */
  setValues(values: Record<string, any>): void;
  /** Shows or hides a field. Hidden fields are ignored by validation and submit mapping. */
  setVisible(fieldName: string, visible: boolean): void;
  /** Makes a field required or optional. */
  setRequired(fieldName: string, required: boolean): void;
  /** Makes a field read-only or editable. */
  setReadonly(fieldName: string, readonly: boolean): void;
  /** Enables or disables a field. */
  setDisabled(fieldName: string, disabled: boolean): void;
  /** Overrides the field label for the current runtime session. */
  setLabel(fieldName: string, label: string): void;
  /** Replaces the options of a choice/dropdown field for the current runtime session. */
  setOptions(fieldName: string, options: ServicePortalChoiceOption[]): void;
  /** Validates the full form and populates field errors. */
  validate(): boolean;
  /** Validates fields assigned to a wizard step. */
  validateStep(stepIndex: number): boolean;
  /** Reads current validation errors by field name. */
  getErrors(): Record<string, string[]>;
  /** Adds a validation error below a field. */
  addFieldError(fieldName: string, message: string): void;
  /** Clears validation errors for a field. */
  clearFieldError(fieldName: string): void;
  /** Adds an informational helper message below a field. */
  addFieldInfo(fieldName: string, message: string): void;
  /** Clears informational helper messages for a field. */
  clearFieldInfo(fieldName: string): void;
  /** Reads the active wizard step index. */
  getCurrentStep(): number;
  /** Reads the total wizard step count, or 1 for a single-step form. */
  getStepCount(): number;
  /** Moves to a specific wizard step. The index is zero-based. */
  goToStep(stepIndex: number): void;
  /** Validates the current step and moves forward when valid. */
  nextStep(): boolean;
  /** Moves to the previous wizard step. */
  previousStep(): boolean;
  /** Reads field-to-column mapping configured through prm:map. */
  getMapping(): Record<string, string>;
  /** Reads the target table configured on prm-form. */
  getTargetTable(): string;
  /** Submits the form through the same flow as the submit button. */
  submit(): Promise<unknown> | unknown;
  /** Registers a field change listener. Use "*" to listen to all fields. */
  onFieldChange(fieldName: string | "*", handler: (value: any, previousValue: any) => void): void;
  /** Registers a wizard step change listener. */
  onStepChange(handler: (fromStep: number, toStep: number) => void): void;
}

interface ServicePortalFormChangeEvent {
  /** Changed field name. */
  field: string;
  /** New field value. */
  value: any;
  /** Previous field value. */
  previousValue: any;
}

interface ServicePortalFormSubmitEvent {
  /** Current form values before mapping. Mutating this does not change the final record directly. */
  values: Record<string, any>;
  /** Field-to-column mapping used by submit. */
  mapping: Record<string, string>;
  /** Final record payload sent to Gate.api.record.create/update. Mutate this object to set fields not present in the form. */
  record: Record<string, any>;
}

interface ServicePortalFormSuccessEvent {
  /** Response record returned by Gate.api.record.create/update. */
  record: any;
  /** Raw response returned by the Gate record API. */
  response: any;
}

interface ServicePortalFormErrorEvent {
  /** Submit error raised by validation, API or custom handler code. */
  error: any;
}

interface ServicePortalFormEvents {
  /** Runs after prm-form creates Gate.form and wires the form store. The form parameter is the ServicePortalFormApi. */
  onLoad?(form: ServicePortalFormApi): void | Promise<void>;
  /** Runs whenever a field value changes. event.field is the changed field name and event.value is the new value. */
  onChange?(form: ServicePortalFormApi, event: ServicePortalFormChangeEvent): void | Promise<void>;
  /** Runs before submit. Mutate event.record to change the final payload; return false to cancel create/update. */
  onSubmit?(form: ServicePortalFormApi, event: ServicePortalFormSubmitEvent): boolean | void | Promise<boolean | void>;
  /** Runs after create/update succeeds. event.record contains the created/updated record response. */
  onSuccess?(form: ServicePortalFormApi, event: ServicePortalFormSuccessEvent): void | Promise<void>;
  /** Runs when submit fails. event.error contains the thrown validation/API/custom error. */
  onError?(form: ServicePortalFormApi, event: ServicePortalFormErrorEvent): void | Promise<void>;
}

interface ServicePortalFormCatalog {
  id?: string;
  name?: string;
  title?: string;
  slug?: string;
  target_table?: string;
  template_html?: string;
  script_code?: string;
  style_code?: string;
  [key: string]: any;
}

interface ServicePortalFormComponentThis {
  Gate: GateRuntime;
  formCatalog: ServicePortalFormCatalog | null;
  formEvents?: ServicePortalFormEvents;
  [key: string]: any;
}

interface ServicePortalFormComponentOptions {
  name?: string;
  props?: Record<string, any>;
  components?: Record<string, any>;
  data?(this: ServicePortalFormComponentThis): Record<string, any>;
  computed?: Record<string, (this: ServicePortalFormComponentThis) => any>;
  methods?: Record<string, (this: ServicePortalFormComponentThis, ...args: any[]) => any>;
  created?(this: ServicePortalFormComponentThis): void;
  mounted?(this: ServicePortalFormComponentThis): void;
  beforeUnmount?(this: ServicePortalFormComponentThis): void;
  unmounted?(this: ServicePortalFormComponentThis): void;
  template?: string;
  render?: (...args: any[]) => any;
  [key: string]: any;
}

interface GateRuntime {
  /** Form API exposed after prm-form is mounted. */
  form: ServicePortalFormApi;
}

/** Form API available as the first parameter of formEvents handlers and as Gate.form after prm-form mounts. */
declare const form: ServicePortalFormApi;
/** Service form catalog injected by the Service Portal host. */
declare const formCatalog: ServicePortalFormCatalog | null;
/** Shape expected by prm:events="formEvents". Define this object inside data(). */
declare const formEvents: ServicePortalFormEvents;
`;

const snippet = (label: string, insertText: string, detail?: string, documentation?: string): CodeEditorSnippet => ({
    label,
    detail,
    documentation,
    insertText,
});

const emailSendSnippet = snippet(
    "Email send",
    "await email.send({\n  account: \"${1:support}\",\n  to: \"${2:recipient@example.com}\",\n  subject: \"${3:Subject}\",\n  html: \"<p>${4:Message}</p>\",\n  related: { table: \"${5:table_name}\", id: \"${6:record_id}\" }\n});",
    "Queue an outbound email message",
    "Requires capability email.send. Use related to track the email from a platform record."
);

const emailTemplateSnippet = snippet(
    "Email send template",
    "await email.sendTemplate({\n  account: \"${1:support}\",\n  template: \"${2:template_code}\",\n  to: \"${3:recipient@example.com}\",\n  record: { table: \"${4:table_name}\", id: \"${5:record_id}\" },\n  data: {\n    ${6:customMessage}: \"${7:Message}\"\n  }\n});",
    "Render a template and queue an outbound email message",
    "Requires capability email.send_template. Template placeholders can use the record and data values."
);

const emailRenderTemplateSnippet = snippet(
    "Email render template",
    "const renderedEmail = await email.renderTemplate({\n  template: \"${1:template_code}\",\n  record: { table: \"${2:table_name}\", id: \"${3:record_id}\" },\n  data: {\n    ${4:customMessage}: \"${5:Message}\"\n  }\n});",
    "Render an email template without sending it",
    "Requires capability email.render_template."
);

const backendEmailSnippets = [emailSendSnippet, emailTemplateSnippet, emailRenderTemplateSnippet];

const attachmentListSnippet = snippet(
    "Attachments list",
    "const files = await attachments.list({\n  table: \"${1:table_name}\",\n  record_id: \"${2:record_id}\",\n  column: ${3:null}\n});",
    "List attachments from a backend runtime",
    "Requires capability attachment.read:<table>. Omit column for record-level attachments."
);

const attachmentReadSnippet = snippet(
    "Attachment read base64",
    "const file = await attachments.read({\n  table: \"${1:table_name}\",\n  record_id: \"${2:record_id}\",\n  attachment_id: \"${3:attachment_id}\"\n});",
    "Read attachment content as base64",
    "Requires capability attachment.read:<table>."
);

const attachmentCreateBase64Snippet = snippet(
    "Attachment create from base64",
    "const attachment = await attachments.createBase64({\n  table: \"${1:table_name}\",\n  record_id: \"${2:record_id}\",\n  filename: \"${3:file.txt}\",\n  content_type: \"${4:text/plain}\",\n  base64: \"${5:SGVsbG8=}\"\n});",
    "Create an attachment from base64 in a backend runtime",
    "Requires capability attachment.write:<table>."
);

const backendAttachmentSnippets = [attachmentListSnippet, attachmentReadSnippet, attachmentCreateBase64Snippet];

const backendVersionRecordRestoreSnippet = snippet(
    "Version record restore",
    "const restoredVersion = await versionRecords.restore({\n  table: \"${1:table_name}\",\n  record_id: \"${2:record_id}\",\n  version_record_id: \"${3:version_record_id}\"\n});",
    "Restore a version-controlled record from a VersionRecord snapshot",
    "Requires capability version_record.restore:<table>."
);

const backendVersionRecordSnippets = [backendVersionRecordRestoreSnippet];

const clientEmailSendSnippet = snippet(
    "Client email send",
    "await client.email.send({\n  account: \"${1:support}\",\n  to: \"${2:recipient@example.com}\",\n  subject: \"${3:Subject}\",\n  html: \"<p>${4:Message}</p>\",\n  related: { table: tableName, id: current.id }\n});",
    "Queue an outbound email from a client action",
    "Uses the authenticated Dynamic API endpoint through client.email.send."
);

const clientEmailTemplateSnippet = snippet(
    "Client email send template",
    "await client.email.sendTemplate({\n  account: \"${1:support}\",\n  template: \"${2:template_code}\",\n  to: \"${3:recipient@example.com}\",\n  record: { table: tableName, id: current.id },\n  data: {\n    ${4:customMessage}: \"${5:Message}\"\n  }\n});",
    "Send an email template from a client action",
    "Uses the authenticated Dynamic API endpoint through client.email.sendTemplate."
);

const clientEmailSnippets = [clientEmailSendSnippet, clientEmailTemplateSnippet];

const clientVersionRecordRestoreSnippet = snippet(
    "Client restore version record",
    "await client.dynamicApi.post(\"platform/version-records/restore\", {\n  table: \"${1:table_name}\",\n  record_id: \"${2:record_id}\",\n  version_record_id: \"${3:version_record_id}\"\n});",
    "Restore a record through the metadata-driven version record Dynamic API"
);

const clientVersionRecordSnippets = [clientVersionRecordRestoreSnippet];

const clientModalOpenSnippet = snippet(
    "Client modal open",
    "const result = await ui.modal.open({\n  widget: \"${1:widget_code}\",\n  title: \"${2:Modal title}\",\n  size: \"${3:md}\",\n  data: {\n    tableName,\n    record: current\n  }\n});\n\nif (result.action === \"confirm\") {\n  ${4:// use result.data}\n}",
    "Open a Gate Widget modal and wait for the result",
    "The widget can read Gate.modal.data and resolve with Gate.modal.submit(data) or Gate.modal.close(...)."
);

const clientModalSnippets = [clientModalOpenSnippet];

const clientReturnToSnippet = snippet(
    "Router return to previous context",
    "router.returnTo({ fallback: `/home/workspace/${tableName}/record/${current.id}` });",
    "Navigate to the safe return_to URL, with a fallback when the form was opened directly",
    "Useful after create/update actions launched from lists, related lists, or other dynamic contexts."
);

const clientOpenRecordWithReturnSnippet = snippet(
    "Router open record with return",
    "router.toRecord(\"${1:table_name}\", ${2:null}, { includeReturnTo: true });",
    "Open a record form and preserve the current URL as return_to",
    "Use this from actions or client scripts when the next form should return to the current context."
);

const clientNavigationSnippets = [clientReturnToSnippet, clientOpenRecordWithReturnSnippet];

const clientFieldInfoSnippet = snippet(
    "Client field info",
    "form.addFieldInfo(\"${1:field_name}\", \"${2:Informational message}\");",
    "Show a translated informational helper message below a field",
    "The first argument autocompletes from the script target table when metadata is available."
);

const clientFormSnippets = [clientFieldInfoSnippet];

const clientAttachmentListSnippet = snippet(
    "Client attachments list",
    "const files = await client.attachments.list({\n  table: tableName,\n  recordId: current.id,\n  column: ${1:null}\n});",
    "List attachments from a client action",
    "Uses the authenticated Records attachment API."
);

const clientAttachmentUploadSnippet = snippet(
    "Client attachment upload",
    "const input = document.createElement(\"input\");\ninput.type = \"file\";\ninput.onchange = async () => {\n  const file = input.files?.[0];\n  if (!file) return;\n\n  await client.attachments.upload({\n    table: tableName,\n    recordId: current.id,\n    file,\n    column: ${1:null}\n  });\n};\ninput.click();",
    "Upload a browser File from a client action",
    "Use draft helpers when the record has not been created yet."
);

const clientAttachmentViewUrlSnippet = snippet(
    "Client attachment view URL",
    "const imageUrl = client.attachments.viewUrl({\n  table: tableName,\n  recordId: current.id,\n  attachmentId: \"${1:attachment_id}\",\n  column: ${2:null}\n});",
    "Build an inline image URL from a client action",
    "Public attachments can be rendered without authentication by the generated URL."
);

const clientAttachmentSnippets = [clientAttachmentListSnippet, clientAttachmentUploadSnippet, clientAttachmentViewUrlSnippet];

const defaultValueHandlerSnippet = snippet(
    "Default value handler",
    "/**\n * @param {DefaultValueScriptContext} context\n */\nasync function handler({ current, tableName, client, ui, router, user, utils, column, defaultValue }) {\n  return ${1:defaultValue};\n}",
    "Calculate this field default value for new records",
    "Return the value that should be applied to the column. Scripts run before Client Scripts onLoad."
);

const defaultValueCurrentUserSnippet = snippet(
    "Default current user",
    "/**\n * @param {DefaultValueScriptContext} context\n */\nasync function handler({ user }) {\n  return user?.id || null;\n}",
    "Use the authenticated user id as the default value"
);

const defaultValueNowSnippet = snippet(
    "Default now",
    "/**\n * @param {DefaultValueScriptContext} context\n */\nasync function handler({ utils }) {\n  return utils.now();\n}",
    "Use the current timestamp as the default value"
);

const defaultValueClientQuerySnippet = snippet(
    "Default from query",
    "/**\n * @param {DefaultValueScriptContext} context\n */\nasync function handler({ client }) {\n  const record = await client.db.table(\"${1:table_name}\").first({\n    where: { ${2:active}: true }\n  });\n\n  return record?.${3:id} || null;\n}",
    "Read a browser-accessible record and use it as the default value"
);

const defaultValueSnippets = [
    defaultValueHandlerSnippet,
    defaultValueCurrentUserSnippet,
    defaultValueNowSnippet,
    defaultValueClientQuerySnippet,
];

const gateEmailSendSnippet = snippet(
    "Gate email send",
    "await Gate.api.email.send({\n  account: \"${1:support}\",\n  to: \"${2:recipient@example.com}\",\n  subject: \"${3:Subject}\",\n  html: \"<p>${4:Message}</p>\"\n});",
    "Queue an outbound email from a Gate page or widget",
    "Requires api_scope_json to include email.send."
);

const gateEmailTemplateSnippet = snippet(
    "Gate email send template",
    "await Gate.api.email.sendTemplate({\n  account: \"${1:support}\",\n  template: \"${2:template_code}\",\n  to: \"${3:recipient@example.com}\",\n  record: { table: \"${4:table_name}\", id: \"${5:record_id}\" },\n  data: {\n    ${6:customMessage}: \"${7:Message}\"\n  }\n});",
    "Send an email template from a Gate page or widget",
    "Requires api_scope_json to include email.send_template."
);

const gateEmailSnippets = [gateEmailSendSnippet, gateEmailTemplateSnippet];

const gateAttachmentListSnippet = snippet(
    "Gate attachments list",
    "const files = await Gate.api.attachments.list({\n  table: \"${1:table_name}\",\n  recordId: \"${2:record_id}\",\n  column: ${3:null}\n});",
    "List attachments from a Gate page or widget",
    "Requires api_scope_json to include attachment.list."
);

const gateAttachmentUploadSnippet = snippet(
    "Gate attachment upload",
    "const input = document.createElement(\"input\");\ninput.type = \"file\";\ninput.onchange = async () => {\n  const file = input.files?.[0];\n  if (!file) return;\n\n  const attachment = await Gate.api.attachments.upload({\n    table: \"${1:table_name}\",\n    recordId: \"${2:record_id}\",\n    file,\n    column: ${3:null}\n  });\n};\ninput.click();",
    "Upload a File selected in a Gate page or widget",
    "Requires api_scope_json to include attachment.upload."
);

const gateDraftAttachmentUploadSnippet = snippet(
    "Gate draft attachment upload",
    "const draftToken = crypto.randomUUID().replaceAll(\"-\", \"\");\nconst input = document.createElement(\"input\");\ninput.type = \"file\";\ninput.onchange = async () => {\n  const file = input.files?.[0];\n  if (!file) return;\n\n  await Gate.api.attachments.upload({\n    table: \"${1:table_name}\",\n    draftToken,\n    file,\n    column: ${2:null}\n  });\n};\ninput.click();",
    "Upload a draft attachment before the record exists",
    "Send _attachment_draft_token when creating the record so the backend promotes the files."
);

const gateAttachmentViewUrlSnippet = snippet(
    "Gate attachment view URL",
    "const imageUrl = await Gate.api.attachments.viewUrl({\n  table: \"${1:table_name}\",\n  recordId: \"${2:record_id}\",\n  attachmentId: \"${3:attachment_id}\",\n  column: ${4:null}\n});",
    "Build an inline image URL for use in img src or platform components",
    "Requires api_scope_json to include attachment.view_url. Public attachments can be rendered without authentication by the generated URL."
);

const gateAttachmentSnippets = [
    gateAttachmentListSnippet,
    gateAttachmentUploadSnippet,
    gateDraftAttachmentUploadSnippet,
    gateAttachmentViewUrlSnippet,
];

const gateThemeSnippet = snippet(
    "Gate theme colors",
    "const theme = Gate.theme;\nconst primarySeedColor = theme.primary_seed_color || \"var(--prm-primary)\";\nconst surfaceColor = \"var(--prm-surface)\";\n\n$1",
    "Read the active platform theme inside a Gate page or widget",
    "Gate pages also receive CSS variables such as --prm-primary, --prm-bg, --prm-surface, --prm-text-primary and --prm-border."
);

const gateThemeStyleSnippet = snippet(
    "Gate themed style",
    "style: {\n  backgroundColor: \"var(--prm-surface)\",\n  color: \"var(--prm-text-primary)\",\n  borderColor: \"var(--prm-border)\"\n}",
    "Use platform theme CSS variables in Vue component style objects"
);

const gateThemeSnippets = [gateThemeSnippet, gateThemeStyleSnippet];

/* eslint-disable no-template-curly-in-string */
const servicePortalFormScriptSnippet = snippet(
    "Service Portal form script",
    "({\n  data() {\n    return {\n      // To enable these handlers, add prm:events=\"formEvents\" to <prm-form>.\n      formEvents: {\n        /** @param {ServicePortalFormApi} form */\n        onLoad(form) {\n          ${1:// Configure the form after it loads.}\n        },\n        /** @param {ServicePortalFormApi} form @param {ServicePortalFormChangeEvent} event */\n        onChange(form, event) {\n          if (event.field === \"${2:field_name}\") {\n            ${3:// React to event.value and event.previousValue.}\n          }\n        },\n        /** @param {ServicePortalFormApi} form @param {ServicePortalFormSubmitEvent} event */\n        onSubmit(form, event) {\n          ${4:// Return false to cancel submit.}\n          return true;\n        },\n        /** @param {ServicePortalFormApi} form @param {ServicePortalFormSuccessEvent} event */\n        onSuccess(form, event) {\n          ${5:// event.record is the created or updated record response.}\n        },\n        /** @param {ServicePortalFormApi} form @param {ServicePortalFormErrorEvent} event */\n        onError(form, event) {\n          Gate.ui.notify(event.error?.message || \"Form submission failed\", \"error\");\n        }\n      }\n    };\n  },\n  mounted() {\n    if (Gate.form) {\n      ${6:// Gate.form is available after prm-form is mounted.}\n    }\n  }\n})",
    "Vue component object used as Service Portal form script_code",
    "Use prm:events=\"formEvents\" on <prm-form> to connect onLoad/onChange/onSubmit/onSuccess/onError handlers."
);

const servicePortalOnLoadHandlerSnippet = snippet(
    "Service form onLoad handler",
    "/** @param {ServicePortalFormApi} form */\nonLoad(form) {\n  ${1:form.setReadonly(\"field_name\", true);}\n}",
    "Add an onLoad handler inside formEvents",
    "form autocompletes ServicePortalFormApi methods such as getValue, setValue, setRequired and addFieldInfo."
);

const servicePortalOnChangeHandlerSnippet = snippet(
    "Service form onChange handler",
    "/** @param {ServicePortalFormApi} form @param {ServicePortalFormChangeEvent} event */\nonChange(form, event) {\n  if (event.field === \"${1:field_name}\") {\n    ${2:form.setValue(\"target_field\", event.value);}\n  }\n}",
    "Add an onChange handler inside formEvents",
    "event autocompletes field, value and previousValue. Field names autocomplete in event.field comparisons."
);

const servicePortalOnSubmitHandlerSnippet = snippet(
    "Service form onSubmit handler",
    "/** @param {ServicePortalFormApi} form @param {ServicePortalFormSubmitEvent} event */\nonSubmit(form, event) {\n  if (!event.values.${1:field_name}) {\n    form.addFieldError(\"${1:field_name}\", Gate.i18n.t(\"validation.required\", {}, \"This field is required.\"));\n    return false;\n  }\n\n  return true;\n}",
    "Add an onSubmit handler inside formEvents",
    "Return false to cancel submit. Mutate event.record to change the final record payload."
);

const servicePortalSubmitValidationSnippet = snippet(
    "Service form submit validation",
    "onSubmit(form, event) {\n  if (!form.getValue(\"${1:field_name}\")) {\n    form.addFieldError(\"${1:field_name}\", Gate.i18n.t(\"${2:validation.required}\", {}, \"This field is required.\"));\n    return false;\n  }\n\n  return true;\n}",
    "Validate a Service Portal form before create/update",
    "Place this inside formEvents. Returning false cancels the default submit flow."
);

const servicePortalFieldChangeSnippet = snippet(
    "Service form field change",
    "onChange(form, event) {\n  if (event.field === \"${1:field_name}\" && event.value === \"${2:value}\") {\n    form.setRequired(\"${3:target_field}\", true);\n  }\n}",
    "React to field changes in prm-form",
    "Field names autocomplete from the fields currently configured in the Service Builder."
);

const servicePortalRecordPayloadSnippet = snippet(
    "Service form set record field",
    "event.record.${1:status} = ${2:\"todo\"};",
    "Set a final record field inside onSubmit",
    "Use this inside onSubmit(form, event) to add or override fields that are not visible in the form."
);

const servicePortalRecordQuerySnippet = snippet(
    "Service form record query",
    "const result = await Gate.api.record.query({\n  table: form.getTargetTable() || \"${1:table_name}\",\n  query: {\n    ${2:active}: true\n  },\n  limit: ${3:10}\n});",
    "Query records from a Service Portal form script",
    "Requires the Gate page/widget api_scope_json to allow record.query."
);

const servicePortalNotifySnippet = snippet(
    "Service form notify",
    "await Gate.ui.notify(Gate.i18n.t(\"${1:message.key}\", {}, \"${2:Message}\"), \"${3:success}\");",
    "Show a Gate notification with i18n fallback"
);

const servicePortalNavigateSnippet = snippet(
    "Service form navigate",
    "await Gate.router.navigate(\"${1:/g/request-detail?requestId=}\" + encodeURIComponent(String(${2:recordId})));",
    "Navigate inside the Gate host after custom form logic"
);

const servicePortalI18nSnippet = snippet(
    "Service form i18n",
    "const label = Gate.i18n.t(\"${1:translation.key}\", { ${2:name}: ${3:value} }, \"${4:Fallback text}\");",
    "Translate a Service Portal label/message with fallback text"
);

const servicePortalFormSnippets = [
    servicePortalFormScriptSnippet,
    servicePortalOnLoadHandlerSnippet,
    servicePortalOnChangeHandlerSnippet,
    servicePortalOnSubmitHandlerSnippet,
    servicePortalSubmitValidationSnippet,
    servicePortalFieldChangeSnippet,
    servicePortalRecordPayloadSnippet,
    servicePortalRecordQuerySnippet,
    servicePortalNotifySnippet,
    servicePortalNavigateSnippet,
    servicePortalI18nSnippet,
    ...gateEmailSnippets,
    ...gateAttachmentSnippets,
    ...gateThemeSnippets,
];

/* eslint-enable no-template-curly-in-string */
const profiles: Record<CodeEditorProfileId, Omit<CodeEditorProfile, "targetColumns">> = {
    "business_rule.condition": {
        id: "business_rule.condition",
        language: "javascript",
        label: "Business Rule Condition",
        snippets: [
            snippet(
                "Business rule condition handler",
                "/**\n * @param {BusinessRuleHandlerContext} context\n */\nasync function handler({ current, previous, changes, event, operation, table, user, db, http, secrets, email, attachments, versionRecords, events, log, utils }) {\n  return ${1:true};\n}",
                "Condition handler called by the business rule runtime",
                "Return true to continue or false to skip the action script."
            ),
            ...backendEmailSnippets,
            ...backendAttachmentSnippets,
            ...backendVersionRecordSnippets,
        ],
        extraLib: businessRuleLib,
    },
    "business_rule.action": {
        id: "business_rule.action",
        language: "javascript",
        label: "Business Rule Action",
        snippets: [
            snippet(
                "Business rule action handler",
                "/**\n * @param {BusinessRuleHandlerContext} context\n */\nasync function handler({ current, previous, changes, event, operation, table, user, db, http, secrets, email, attachments, versionRecords, events, log, utils, rule }) {\n  return rule.ok({ current });\n}",
                "Action handler called by the business rule runtime",
                "Return rule.ok(), rule.skip(), or rule.abort(). Destructure only the runtime APIs you need."
            ),
            snippet("Abort with message", "return rule.abort(\"${1:Operation aborted}\");", "Abort the current record operation"),
            snippet(
                "Emit event",
                "const emitted = await events.emit('${1:custom.domain.event}', { ${2:key}: ${3:value} }, { correlationKey: ${4:current.id} });",
                "Emit an active catalog event (requires events.emit capability)"
            ),
            snippet(
                "DB advanced query",
                "const records = await db.table(\"${1:table_name}\").all({\n  where: {\n    type: \"logical\",\n    operator: \"and\",\n    conditions: [\n      { type: \"condition\", field: \"${2:name}\", operator: \"stw\", value: \"${3:PRM}\" },\n      { type: \"condition\", field: \"${4:created_at}\", operator: \"between\", value: [\"${5:2026-05-01T00:00:00Z}\", \"${6:2026-05-04T23:59:59Z}\"] }\n    ]\n  },\n  order: [\n    { field: \"${7:priority}\", direction: \"desc\" },\n    { field: \"${8:created_at}\", direction: \"asc\" }\n  ]\n});",
                "Query records with operators and multi-field ordering"
            ),
            snippet(
                "DB count",
                "const count = await db.table(\"${1:table_name}\").count({\n  where: {\n    type: \"condition\",\n    field: \"${2:status}\",\n    operator: \"eq\",\n    value: \"${3:active}\"\n  }\n});",
                "Count matching records without loading them"
            ),
            ...backendEmailSnippets,
            ...backendAttachmentSnippets,
            ...backendVersionRecordSnippets,
        ],
        extraLib: businessRuleLib,
    },
    "fluss.script_task": {
        id: "fluss.script_task",
        language: "javascript",
        label: "Fluss Script Task",
        snippets: [
            snippet(
                "Script task handler",
                FLUSS_SCRIPT_TASK_DEFAULT_SCRIPT.replace("outputs: {}", "outputs: { ${1:result}: ${2:value} }"),
                "Handler called when the Fluss token enters the Script Task",
                "Return rule.ok(), rule.skip(), or rule.abort(). outputs become Fluss variables."
            ),
            snippet("Set outputs", "return rule.ok({ outputs: { ${1:name}: ${2:value} } });", "Expose outputs as Fluss variables"),
            snippet("Abort Script Task", "return rule.abort(\"${1:Script Task aborted}\");", "Create a Fluss incident")
        ],
        extraLib: flussScriptTaskLib,
    },
    "platform_job.condition": {
        id: "platform_job.condition",
        language: "javascript",
        label: "Platform Job Condition",
        snippets: [
            snippet(
                "Platform job condition handler",
                "/**\n * @param {PlatformJobHandlerContext} context\n */\nasync function handler({ job, db, http, secrets, email, attachments, versionRecords, log, utils, schedule, execution, user }) {\n  return ${1:true};\n}",
                "Condition handler called before the platform job action script",
                "Return true to continue or false to skip this job execution."
            ),
            ...backendEmailSnippets,
            ...backendAttachmentSnippets,
            ...backendVersionRecordSnippets,
        ],
        extraLib: platformJobLib,
    },
    "platform_job.action": {
        id: "platform_job.action",
        language: "javascript",
        label: "Platform Job Action",
        snippets: [
            snippet(
                "Platform job action handler",
                "/**\n * @param {PlatformJobHandlerContext} context\n */\nasync function handler({ job, db, http, secrets, email, attachments, versionRecords, log, utils, schedule, execution, user }) {\n  log.info(\"Platform job started\", { execution_id: execution.id });\n  return job.ok();\n}",
                "Action handler called by the Platform Jobs runtime",
                "Return job.ok(), job.skip(), or job.fail(). Destructure only the runtime APIs you need."
            ),
            snippet("Skip execution", "return job.skip(\"${1:Skipped by condition}\");", "Skip this execution without failing"),
            snippet("Fail execution", "return job.fail(\"${1:Job failed}\");", "Mark this execution as failed"),
            snippet(
                "DB dot-walk query",
                "const records = await db.table(\"${1:sys_columns}\").all({\n  where: {\n    type: \"condition\",\n    field: \"${2:sys_table_id.name}\",\n    operator: \"eq\",\n    value: \"${3:sys_users}\"\n  },\n  order: [\n    { field: \"${4:sys_table_id.name}\", direction: \"asc\" },\n    { field: \"${5:order}\", direction: \"asc\" }\n  ]\n});",
                "Query through a reference field and sort by multiple fields"
            ),
            snippet(
                "DB count",
                "const count = await db.table(\"${1:sys_job_executions}\").count({\n  where: {\n    type: \"condition\",\n    field: \"${2:status}\",\n    operator: \"eq\",\n    value: \"${3:success}\"\n  }\n});",
                "Count matching records without loading them"
            ),
            ...backendEmailSnippets,
            ...backendAttachmentSnippets,
            ...backendVersionRecordSnippets,
        ],
        extraLib: platformJobLib,
    },
    "form_action.form": {
        id: "form_action.form",
        language: "javascript",
        label: "Form Action",
        snippets: [
            snippet("Form action handler", "/**\n * @param {ClientActionContext} context\n */\nasync function handler({ current, tableName, client, ui, router, user, utils }) {\n  ui.info(\"${1:ACTION}\", \"${2:Action executed}\");\n}"),
            ...clientModalSnippets,
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: formActionLib,
    },
    "form_action.list": {
        id: "form_action.list",
        language: "javascript",
        label: "List Action",
        snippets: [
            snippet("List action handler", "/**\n * @param {ClientActionContext} context\n */\nasync function handler({ records, table, client, ui, router, user, utils }) {\n  if (!records.length) {\n    ui.warning(\"NO_SELECTION\", \"Select at least one record\");\n    return;\n  }\n\n  $1\n}"),
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: formActionLib,
    },
    "form_action.related_form": {
        id: "form_action.related_form",
        language: "javascript",
        label: "Related Form Action",
        snippets: [
            snippet("Related form action handler", "/**\n * @param {ClientActionContext} context\n */\nasync function handler({ current, tableName, client, ui, router, user, utils }) {\n  $1\n}"),
            ...clientModalSnippets,
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: formActionLib,
    },
    "form_action.related_list": {
        id: "form_action.related_list",
        language: "javascript",
        label: "Related List Action",
        snippets: [
            snippet("Related list action handler", "/**\n * @param {ClientActionContext} context\n */\nasync function handler({ records, table, client, ui, router, user, utils }) {\n  $1\n}"),
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: formActionLib,
    },
    "default_value.script": {
        id: "default_value.script",
        language: "javascript",
        label: "Default Value Script",
        snippets: [
            ...defaultValueSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: defaultValueScriptLib,
    },
    "client_script.onload": {
        id: "client_script.onload",
        language: "javascript",
        label: "Client Script onLoad",
        snippets: [
            snippet(
                "Client script onLoad handler",
                "/**\n * @param {ClientScriptContext} context\n */\nasync function handler({ current, tableName, client, ui, router, user, utils, form, event }) {\n  ${1:// Configure the form after it loads.}\n}",
                "Runs when the record form is loaded"
            ),
            ...clientFormSnippets,
            ...clientModalSnippets,
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: clientScriptLib,
    },
    "client_script.onsubmit": {
        id: "client_script.onsubmit",
        language: "javascript",
        label: "Client Script onSubmit",
        snippets: [
            snippet(
                "Client script onSubmit handler",
                "/**\n * @param {ClientScriptContext} context\n */\nasync function handler({ current, tableName, client, ui, router, user, utils, form, event }) {\n  if (!form.getValue(\"${1:name}\")) {\n    form.addFieldError(\"${1:name}\", \"${2:This field is required.}\");\n    return false;\n  }\n\n  return true;\n}",
                "Runs before form actions that respect form validation. Return false to block submit."
            ),
            ...clientFormSnippets,
            ...clientModalSnippets,
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: clientScriptLib,
    },
    "client_script.onchange": {
        id: "client_script.onchange",
        language: "javascript",
        label: "Client Script onChange",
        snippets: [
            snippet(
                "Client script onChange handler",
                "/**\n * @param {ClientScriptContext} context\n */\nasync function handler({ current, previous, tableName, client, ui, router, user, utils, form, event }) {\n  if (event.field === \"${1:state}\" && event.newValue === \"${2:closed}\") {\n    form.setRequired(\"${3:close_notes}\", true);\n  }\n}",
                "Runs when the configured field changes"
            ),
            ...clientFormSnippets,
            ...clientModalSnippets,
            ...clientNavigationSnippets,
            ...clientEmailSnippets,
            ...clientAttachmentSnippets,
            ...clientVersionRecordSnippets,
        ],
        extraLib: clientScriptLib,
    },
    "dynamic_api.handler": {
        id: "dynamic_api.handler",
        language: "javascript",
        label: "Dynamic API Handler",
        snippets: [
            snippet(
                "Dynamic API handler",
                "/**\n * @param {DynamicApiHandlerContext} context\n */\nasync function handler({ request, response, db, http, secrets, email, attachments, versionRecords, log, utils, auth, params }) {\n  return response.json({ ok: true });\n}",
                "Handler called by the Dynamic API runtime",
                "Return response.json(), response.text(), response.html(), or response.empty()."
            ),
            snippet(
                "DB advanced response",
                "const records = await db.table(\"${1:table_name}\").all({\n  where: {\n    type: \"condition\",\n    field: \"${2:name}\",\n    operator: \"contains\",\n    value: request.query.${3:q} || \"\"\n  },\n  order: [\n    { field: \"${4:name}\", direction: \"asc\" }\n  ],\n  limit: ${5:50}\n});\n\nreturn response.json({ records });",
                "Return records queried with the advanced db.table where format"
            ),
            snippet(
                "DB count response",
                "const count = await db.table(\"${1:table_name}\").count({\n  where: {\n    type: \"condition\",\n    field: \"${2:name}\",\n    operator: \"contains\",\n    value: request.query.${3:q} || \"\"\n  }\n});\n\nreturn response.json({ count });",
                "Return a count without loading matching records"
            ),
            snippet(
                "Restore version record",
                "const restoredVersion = await versionRecords.restore({\n  table: \"${1:table_name}\",\n  record_id: \"${2:record_id}\",\n  version_record_id: \"${3:version_record_id}\"\n});\n\nreturn response.json({ data: restoredVersion });",
                "Restore a version-controlled record using the guarded version_record.restore capability"
            ),
            ...backendEmailSnippets,
            ...backendAttachmentSnippets,
            ...backendVersionRecordSnippets,
        ],
        extraLib: dynamicApiLib,
    },
    "gate.page.component": {
        id: "gate.page.component",
        language: "javascript",
        label: "Gate Page Component",
        snippets: [
            snippet("Gate page component", "({\n  data() {\n    return {\n      loading: false\n    };\n  },\n  methods: {\n    async load() {\n      $1\n    }\n  },\n  mounted() {\n    this.load();\n  }\n})"),
            ...gateEmailSnippets,
            ...gateAttachmentSnippets,
            ...gateThemeSnippets,
        ],
        extraLib: gateLib,
    },
    "gate.widget.component": {
        id: "gate.widget.component",
        language: "javascript",
        label: "Gate Widget Component",
        snippets: [
            snippet("Gate widget component", "({\n  props: {\n    title: {\n      type: String,\n      default: \"\"\n    }\n  },\n  methods: {\n    go(to) {\n      return Gate.router.navigate(to);\n    }\n  }\n})"),
            ...gateEmailSnippets,
            ...gateAttachmentSnippets,
            ...gateThemeSnippets,
        ],
        extraLib: gateLib,
    },
    "service_portal.form_script": {
        id: "service_portal.form_script",
        language: "javascript",
        label: "Service Portal Form Script",
        snippets: servicePortalFormSnippets,
        extraLib: servicePortalFormScriptLib,
    },
};

export const getCodeEditorProfile = (profileId: CodeEditorProfileId): CodeEditorProfile => ({
    ...profiles[profileId],
});
